// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import {
  ActionIcon,
  Box,
  Button,
  Group,
  Loader,
  Modal,
  Paper,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import { createReference, normalizeErrorString } from '@medplum/core';
import type { CodeableConcept, Condition, Patient } from '@medplum/fhirtypes';
import { CodeableConceptInput, useMedplum, useMedplumProfile } from '@medplum/react';
import { IconPlus, IconSearch, IconTrash } from '@tabler/icons-react';
import dayjs from 'dayjs';
import { type JSX, useEffect, useMemo, useState } from 'react';
import { withTenantScope } from '../../utils/tenant';

const ICD10_VALUESET = 'http://hl7.org/fhir/sid/icd-10-cm/vs';

function formatDate(value: string | undefined): string {
  if (!value) {
    return '—';
  }
  return dayjs(value).format('MM/DD/YYYY');
}

function getConditionTitle(condition: Condition): string {
  return condition.code?.text ?? condition.code?.coding?.[0]?.display ?? condition.code?.coding?.[0]?.code ?? '—';
}

function getIcd10Code(condition: Condition): string {
  return (
    condition.code?.coding?.find((c) => c.system === 'http://hl7.org/fhir/sid/icd-10-cm')?.code ??
    condition.code?.coding?.[0]?.code ??
    '—'
  );
}

export interface DiagnosesSectionProps {
  diagnoses: Condition[];
  loading: boolean;
}

export function DiagnosesSection({ diagnoses, loading }: DiagnosesSectionProps): JSX.Element {
  const medplum = useMedplum();
  const profile = useMedplumProfile() as Patient | undefined;
  const [query, setQuery] = useState('');
  const [addOpen, setAddOpen] = useState(false);
  const [code, setCode] = useState<CodeableConcept | undefined>();
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<string>();
  const [items, setItems] = useState(diagnoses);

  useEffect(() => setItems(diagnoses), [diagnoses]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) {
      return items;
    }
    return items.filter((d) => `${getConditionTitle(d)} ${getIcd10Code(d)}`.toLowerCase().includes(q));
  }, [items, query]);

  const addCondition = async (): Promise<void> => {
    if (!profile?.id || !code) {
      return;
    }
    setSaving(true);
    try {
      const created = await medplum.createResource<Condition>(
        withTenantScope({
          resourceType: 'Condition',
          subject: createReference(profile),
          code,
          clinicalStatus: {
            coding: [{ system: 'http://terminology.hl7.org/CodeSystem/condition-clinical', code: 'active' }],
          },
          recordedDate: dayjs().format('YYYY-MM-DD'),
        })
      );
      setItems((current) => [created, ...current]);
      setCode(undefined);
      setAddOpen(false);
      showNotification({ color: 'green', message: 'Condition added' });
    } catch (err) {
      showNotification({ color: 'red', message: normalizeErrorString(err), autoClose: false });
    } finally {
      setSaving(false);
    }
  };
  const removeCondition = async (item: Condition): Promise<void> => {
    if (!item.id) {
      return;
    }
    setDeletingId(item.id);
    try {
      await medplum.deleteResource('Condition', item.id);
      setItems((current) => current.filter((entry) => entry.id !== item.id));
      showNotification({ color: 'green', message: 'Condition removed' });
    } catch (err) {
      showNotification({ color: 'red', message: normalizeErrorString(err), autoClose: false });
    } finally {
      setDeletingId(undefined);
    }
  };

  return (
    <Paper withBorder radius="md" shadow="sm" p="xl">
      <Group justify="space-between" mb="md" wrap="wrap">
        <Title order={2}>Conditions</Title>
        <Button leftSection={<IconPlus size={16} />} onClick={() => setAddOpen(true)}>
          Add Condition
        </Button>
        <TextInput
          placeholder="Search…"
          value={query}
          onChange={(e) => setQuery(e.currentTarget.value)}
          leftSection={<IconSearch size={14} />}
          size="sm"
          w={240}
          aria-label="Search diagnoses"
        />
      </Group>

      {loading ? (
        <Stack align="center" py="md">
          <Loader size="sm" />
        </Stack>
      ) : (
        <Box style={{ overflowX: 'auto' }}>
          <Table highlightOnHover withTableBorder>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Condition</Table.Th>
                <Table.Th>ICD-10</Table.Th>
                <Table.Th>Diagnosis Date</Table.Th>
                <Table.Th>Actions</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {filtered.length === 0 ? (
                <Table.Tr>
                  <Table.Td colSpan={4}>
                    <Text ta="center" c="dimmed" py="md">
                      No diagnoses on file.
                    </Text>
                  </Table.Td>
                </Table.Tr>
              ) : (
                filtered.map((condition) => (
                  <Table.Tr key={condition.id}>
                    <Table.Td>{getConditionTitle(condition)}</Table.Td>
                    <Table.Td>{getIcd10Code(condition)}</Table.Td>
                    <Table.Td>{formatDate(condition.recordedDate)}</Table.Td>
                    <Table.Td>
                      <ActionIcon
                        color="red"
                        variant="subtle"
                        aria-label={`Remove condition ${getConditionTitle(condition)}`}
                        loading={deletingId === condition.id}
                        disabled={deletingId === condition.id}
                        onClick={() => removeCondition(condition)}
                      >
                        <IconTrash size={16} />
                      </ActionIcon>
                    </Table.Td>
                  </Table.Tr>
                ))
              )}
            </Table.Tbody>
          </Table>
        </Box>
      )}
      <Modal opened={addOpen} onClose={() => setAddOpen(false)} title="Add Condition" centered>
        <Stack>
          <CodeableConceptInput
            binding={ICD10_VALUESET}
            label="ICD-10 Condition"
            name="condition"
            path="Condition.code"
            placeholder="Search ICD-10 codes…"
            onChange={setCode}
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setAddOpen(false)}>
              Cancel
            </Button>
            <Button onClick={addCondition} loading={saving} disabled={!code}>
              Add
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Paper>
  );
}
